import omit from 'lodash.omit'
import React, { FunctionComponent, PropsWithChildren } from 'react'
import * as ThemeUI from 'theme-ui'
import { Spinner } from '..'

type ButtonVariants =
    | 'blue'
    | 'pink'
    | 'default'
    | 'secondaryLight'
    | 'secondaryDark'
    | 'unstyled'
    | 'onDemandCare'
    | 'filter'
    | 'icon'
    | 'iconLight'

export type ButtonProps = {
    /**
     * button variant for styling
     */
    variant?: ButtonVariants

    /**
     * callback when this button is clicked
     */
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void

    /**
     * disable the button
     */
    disabled?: boolean

    /**
     * when you need to show something was selected
     */
    selected?: boolean

    /**
     * Loading state
     */
    loading?: boolean

    /**
     * force button full width to the container
     * useful for buttons within cards
     * that shouldn't be responsive on their own
     */
    fullWidth?: boolean
} & ThemeUI.ButtonProps & {
        // as="a" is a common use-case and needs props
        // wasn't able to get conditional types working for this
        href?: string
        target?: string
    }

/**
 * This is a Tia button used in webapps.
 * Pink and stretched to full container width by default
 */
export const Button: FunctionComponent<
    React.PropsWithChildren<PropsWithChildren<ButtonProps>>
> = ({
    variant = 'default',
    disabled,
    children,
    fullWidth,
    loading = false,
    onClick,
    ...props
}) => {
    const sx = fullWidth
        ? {
              ...props.sx,
              minWidth: '100%'
          }
        : props.sx

    let internalVariant: ButtonVariants | 'disabled' | 'selectedFilter' =
        variant

    if (disabled) {
        internalVariant = 'disabled'
    } else if (props.selected) {
        internalVariant = 'selectedFilter'
    }

    return (
        <ThemeUI.Button
            onClick={ev => {
                if (onClick && !disabled && !loading) {
                    onClick(ev)
                }
            }}
            variant={internalVariant}
            disabled={disabled}
            sx={{
                ...(sx || {})
            }}
            {...omit(props, 'sx')}
        >
            {loading && <Spinner sx={{ height: '26px' }} color="white" />}
            {!loading && children}
        </ThemeUI.Button>
    )
}
